import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Briefcase,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Globe,
  IndianRupee,
  Link2,
  Linkedin,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Megaphone,
  MessageCircle,
  Pencil,
  RefreshCw,
  Rss,
  Search,
  Send,
  Twitter,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Skeleton } from '@/Components/ui/skeleton';
import { Switch } from '@/Components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import { cn } from '@/lib/utils';

import {
  getBroadcast,
  publishJob,
  recordShare,
  saveListingDetails,
  setChannelEnabled,
} from './broadcastApi';
import ConnectedAccounts from './ConnectedAccounts';
import { buildPublicJobUrl, copyText, shareJob } from './jobShare';

/**
 * BroadcastPanel
 *
 * One place to take a posted job public and get it in front of candidates:
 *
 *   1. Publish      one click makes the job live on the public job page
 *   2. Listed on    where it then appears on its own (careers page, Google
 *                   for Jobs, job aggregators) with an honest status each
 *   3. Details      the structured location / salary / type those listings
 *                   use, pre-filled from the JD for the employer to confirm
 *   4. Accounts     post directly from a connected LinkedIn profile or
 *                   Telegram channel (ConnectedAccounts)
 *   5. Share        tracked links for the employer's own network
 *
 * Statuses come from the server and are deliberately conservative: "Eligible"
 * for Google means the markup is published, not that Google has listed it.
 */

const LISTING_ICONS = {
  careers_page: Globe,
  google_jobs: Search,
  aggregators: Rss,
  telegram_platform: Send,
};

const SHARE_META = {
  linkedin: { icon: Linkedin, tone: 'text-[#0A66C2]' },
  whatsapp: { icon: MessageCircle, tone: 'text-emerald-600' },
  telegram: { icon: Send, tone: 'text-sky-500' },
  x: { icon: Twitter, tone: 'text-foreground' },
  email: { icon: Mail, tone: 'text-muted-foreground' },
  copy_link: { icon: Link2, tone: 'text-muted-foreground' },
};

const STATUS_TONES = {
  live: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  eligible: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_feed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  posted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  needs_details: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-amber-50 text-amber-800 border-amber-200',
  pending: 'bg-muted text-muted-foreground border-border',
  blocked: 'bg-muted text-muted-foreground border-border',
  off: 'bg-muted text-muted-foreground border-border',
};

// Kept in step with COUNTRY_NAMES in services/job_broadcast/structuring.py.
const COUNTRIES = [
  ['IN', 'India'], ['AE', 'United Arab Emirates'], ['AU', 'Australia'], ['BD', 'Bangladesh'],
  ['BH', 'Bahrain'], ['CA', 'Canada'], ['DE', 'Germany'], ['FR', 'France'],
  ['GB', 'United Kingdom'], ['IE', 'Ireland'], ['JP', 'Japan'], ['KW', 'Kuwait'],
  ['LK', 'Sri Lanka'], ['MY', 'Malaysia'], ['NL', 'Netherlands'], ['NP', 'Nepal'],
  ['NZ', 'New Zealand'], ['OM', 'Oman'], ['QA', 'Qatar'], ['SA', 'Saudi Arabia'],
  ['SG', 'Singapore'], ['US', 'United States'], ['ZA', 'South Africa'],
];

const BLOCKING_CODES = new Set(['closed', 'expired', 'no_company']);

const formatAmount = (value, currency) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US');
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function BroadcastPanel({ jobId, onStateChange, className }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null); // 'publish' | 'unpublish' | channel key | null
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Held in a ref so a parent passing a fresh callback each render does not
  // change `load`'s identity and re-trigger the fetch.
  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  const apply = useCallback((next) => {
    setState(next);
    onStateChangeRef.current?.(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      apply(await getBroadcast(jobId));
    } catch (e) {
      setLoadError(
        e.status === 403
          ? "You don't have access to this job."
          : e.status === 404
          ? 'This job could not be found.'
          : e.message,
      );
    } finally {
      setLoading(false);
    }
  }, [jobId, apply]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-read the state in place (no skeleton), for changes made outside this
  // panel's own requests, e.g. a LinkedIn account connected in a popup.
  const refresh = useCallback(async () => {
    try {
      apply(await getBroadcast(jobId));
    } catch (e) {
      /* keep what is on screen; the next action will surface any problem */
    }
  }, [jobId, apply]);

  const shareUrl = useMemo(
    () =>
      state
        ? buildPublicJobUrl({ jobId: state.job.job_id, companySlug: state.job.company_slug })
        : '',
    [state],
  );

  const shareJobInfo = useMemo(
    () =>
      state
        ? {
            title: state.job.title,
            companyName: state.job.company_name,
            location: state.labels.location || state.job.location,
            jobType: state.labels.employment_type || state.job.job_type,
            experience: state.job.experience,
          }
        : null,
    [state],
  );

  if (loading) return <PanelSkeleton className={className} />;

  if (loadError || !state) {
    return (
      <div className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <p className="text-sm font-medium text-foreground">Couldn't load broadcast status</p>
        <p className="max-w-sm text-sm text-muted-foreground">{loadError}</p>
        <Button variant="outline" size="sm" onClick={load} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </div>
    );
  }

  const { job, channels, details, labels, readiness, options } = state;
  const canManage = Boolean(state.can_manage);
  const live = Boolean(channels.live);
  const blockerCodes = channels.blockers.map((b) => b.code);
  const hardBlocker = channels.blockers.find((b) => BLOCKING_CODES.has(b.code));
  const needsCompany = blockerCodes.includes('company_hidden');

  const run = async (key, fn, successMessage) => {
    setBusy(key);
    try {
      apply(await fn());
      if (successMessage) toast.success(successMessage);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const handlePublish = () =>
    run(
      'publish',
      () => publishJob(jobId, { publish: true, includeCompany: needsCompany }),
      'Job published. It is now live on your careers page.',
    );

  const handleUnpublish = () =>
    run(
      'unpublish',
      () => publishJob(jobId, { publish: false }),
      'Job unpublished. It is no longer visible to candidates.',
    );

  const handleToggle = (channel, enabled) =>
    run(channel, () => setChannelEnabled(jobId, channel, enabled));

  const handleCopyLink = async () => {
    try {
      await copyText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy the link. Please copy it manually.');
    }
  };

  const handleShare = async (channel) => {
    try {
      const { copied: wasCopied } = await shareJob(channel, shareJobInfo, shareUrl);
      if (channel === 'linkedin') {
        toast.success(
          wasCopied
            ? 'Post text copied. Paste it into the LinkedIn box that opened.'
            : 'LinkedIn opened. Add your message there.',
        );
      } else if (channel === 'copy_link') {
        toast.success('Tracked link copied.');
      }
    } catch (e) {
      toast.error(
        channel === 'copy_link'
          ? 'Could not copy the link. Select it in the box above and copy it manually.'
          : 'Could not share. Please try again.',
      );
      return;
    }
    // Count the share. Best-effort: the share itself already happened.
    try {
      const result = await recordShare(jobId, channel);
      if (result?.recorded) {
        setState((prev) =>
          prev
            ? {
                ...prev,
                channels: {
                  ...prev.channels,
                  share: prev.channels.share.map((c) =>
                    c.key === channel
                      ? { ...c, share_count: result.share_count, last_shared_at: result.last_shared_at }
                      : c,
                  ),
                },
              }
            : prev,
        );
      }
    } catch (e) {
      /* counting is not worth interrupting the user for */
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Megaphone className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight text-foreground">Broadcast this job</h2>
          <p className="truncate text-sm text-muted-foreground">
            {job.title}
            {job.company_name ? ` · ${job.company_name}` : ''}
          </p>
        </div>
      </div>

      {/* 1. Publish */}
      {live ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
              <CheckCircle2 className="h-4 w-4" />
              Live. Candidates can view this job and apply.
            </div>
            {canManage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUnpublish}
                disabled={busy !== null}
                className="h-8 text-muted-foreground hover:text-foreground"
              >
                {busy === 'unpublish' && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Unpublish
              </Button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              className="h-9 w-full bg-background text-xs sm:w-auto sm:min-w-0 sm:flex-1"
              aria-label="Public job link"
            />
            <Button variant="outline" size="sm" onClick={handleCopyLink} className="h-9 gap-1.5">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button variant="outline" size="sm" asChild className="h-9 gap-1.5">
              <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
                View
              </a>
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Lock className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {hardBlocker ? "This job can't be published yet" : 'Not visible to candidates yet'}
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {hardBlocker
                  ? hardBlocker.message
                  : 'Publish it to open applications and list it on every channel below.'}
              </p>
              {!hardBlocker && needsCompany && canManage && (
                <p className="mt-2 text-xs text-muted-foreground">
                  This also switches on your company careers page. Only jobs you publish appear on it.
                </p>
              )}
              {!canManage && !hardBlocker && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Only the company account or an admin can publish a job.
                </p>
              )}
            </div>
          </div>
          {canManage && !hardBlocker && (
            <Button onClick={handlePublish} disabled={busy !== null} className="mt-4 w-full gap-2 sm:w-auto">
              {busy === 'publish' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Megaphone className="h-4 w-4" />
              )}
              Publish and broadcast
            </Button>
          )}
        </div>
      )}

      {/* 2. Listing channels */}
      <section>
        <SectionTitle>Where this job is listed</SectionTitle>
        <div className="divide-y divide-border rounded-xl border border-border bg-card">
          {channels.listing.map((channel) => {
            const Icon = LISTING_ICONS[channel.key] || Globe;
            const switchable = channel.toggleable && canManage && live && state.storage_ready;
            return (
              <div key={channel.key} className="flex items-start gap-3 p-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{channel.label}</span>
                    <StatusPill status={channel.status} label={channel.status_label} />
                    {channel.applications > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="h-3 w-3" />
                        {plural(channel.applications, 'applicant')}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{channel.description}</p>
                  <p className="mt-1 text-xs text-foreground/80">
                    {channel.detail}
                    {channel.url && (
                      <>
                        {' '}
                        <a
                          href={channel.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
                        >
                          View post
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </>
                    )}
                  </p>
                </div>
                {channel.toggleable && (
                  <Switch
                    checked={channel.enabled}
                    disabled={!switchable || busy !== null}
                    onCheckedChange={(next) => handleToggle(channel.key, next)}
                    aria-label={`List on ${channel.label}`}
                    className="mt-1"
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 3. Listing details */}
      <section>
        <div className="mb-2 flex items-center justify-between gap-2">
          <SectionTitle className="mb-0">Listing details</SectionTitle>
          {canManage && !editing && state.storage_ready && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 gap-1.5 px-2 text-xs">
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          {editing ? (
            <DetailsForm
              jobId={jobId}
              details={details}
              options={options}
              onCancel={() => setEditing(false)}
              onSaved={(next) => {
                apply(next);
                setEditing(false);
                toast.success('Listing details saved.');
              }}
            />
          ) : (
            <DetailsSummary
              details={details}
              labels={labels}
              options={options}
              readiness={readiness}
              canEdit={canManage && state.storage_ready}
              onEdit={() => setEditing(true)}
            />
          )}
        </div>
      </section>

      {/* 4. Connected accounts (shown only for providers the server has set up) */}
      <ConnectedAccounts
        jobId={jobId}
        social={state.social}
        live={live}
        canManage={canManage}
        onState={apply}
        onReload={refresh}
      />

      {/* 5. Share */}
      <section>
        <SectionTitle>Share with your network</SectionTitle>
        <p className="-mt-1 mb-2 text-xs text-muted-foreground">
          {live
            ? 'Each button uses its own tracked link, so you can see which channel brings applicants.'
            : 'Publish the job first. A link to an unpublished job shows candidates an error.'}
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {channels.share.map((channel) => {
            const meta = SHARE_META[channel.key] || SHARE_META.copy_link;
            const Icon = meta.icon;
            const stats = [
              channel.share_count > 0 ? `Shared ${channel.share_count}×` : null,
              channel.applications > 0 ? plural(channel.applications, 'applicant') : null,
            ].filter(Boolean);
            return (
              <button
                key={channel.key}
                type="button"
                disabled={!live}
                onClick={() => handleShare(channel.key)}
                title={
                  channel.last_shared_at ? `Last shared ${timeAgo(channel.last_shared_at)}` : undefined
                }
                className={cn(
                  'flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-colors',
                  'hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:bg-card',
                )}
              >
                <Icon className={cn('h-4 w-4 shrink-0', meta.tone)} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">{channel.label}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {stats.length ? stats.join(' · ') : 'Not shared yet'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {channels.tracked_applications > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            {plural(channels.tracked_applications, 'application')} tracked across channels so far
            {channels.other_applications > 0
              ? `, including ${channels.other_applications} from other links.`
              : '.'}
          </p>
        )}
      </section>
    </div>
  );
}

function SectionTitle({ children, className }) {
  return (
    <h3 className={cn('mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground', className)}>
      {children}
    </h3>
  );
}

function StatusPill({ status, label }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
        STATUS_TONES[status] || STATUS_TONES.off,
      )}
    >
      {label}
    </span>
  );
}

function salaryLabel(details, options) {
  const amounts = [details.salary_min, details.salary_max]
    .map((v) => formatAmount(v, details.salary_currency))
    .filter(Boolean)
    .join(' to ');
  if (!amounts) return '';
  const period = (options.salary_periods.find((o) => o.value === details.salary_period) || {}).label;
  return [details.salary_currency, amounts, period].filter(Boolean).join(' ');
}

function DetailsSummary({ details, labels, options, readiness, canEdit, onEdit }) {
  const derived = !details.confirmed;
  const items = [
    { icon: MapPin, label: 'Location', value: labels.location },
    { icon: Globe, label: 'Workplace', value: labels.workplace_type },
    { icon: Briefcase, label: 'Employment', value: labels.employment_type },
    { icon: IndianRupee, label: 'Salary', value: salaryLabel(details, options) },
  ];

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {items.map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-start gap-2.5">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
              <dd className={cn('text-sm', value ? 'text-foreground' : 'text-muted-foreground')}>
                {value || 'Not set'}
              </dd>
            </div>
          </div>
        ))}
      </dl>

      {readiness.missing.length > 0 && (
        <Notice tone="amber" title="Needed before search engines can list this job">
          {readiness.missing.map((m) => (
            <li key={m.field}>{m.message}</li>
          ))}
        </Notice>
      )}

      {readiness.missing.length === 0 && derived && (
        <Notice tone="muted" title="Filled in from your job description">
          <li>
            Check these are right.{' '}
            {canEdit && (
              <button type="button" onClick={onEdit} className="font-medium text-primary underline-offset-2 hover:underline">
                Review details
              </button>
            )}
          </li>
        </Notice>
      )}

      {readiness.warnings.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {readiness.warnings.map((w) => (
            <li key={w.field} className="flex gap-1.5">
              <span aria-hidden="true">•</span>
              <span>{w.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Notice({ tone, title, children }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2 text-xs',
        tone === 'amber'
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-border bg-muted/50 text-muted-foreground',
      )}
    >
      <p className="font-medium">{title}</p>
      <ul className="mt-1 space-y-0.5">{children}</ul>
    </div>
  );
}

function DetailsForm({ jobId, details, options, onCancel, onSaved }) {
  const [form, setForm] = useState({
    city: details.city || '',
    region: details.region || '',
    country_code: details.country_code || 'IN',
    postal_code: details.postal_code || '',
    workplace_type: details.workplace_type || 'onsite',
    employment_type: details.employment_type || 'FULL_TIME',
    salary_min: details.salary_min ?? '',
    salary_max: details.salary_max ?? '',
    salary_currency: details.salary_currency || 'INR',
    salary_period: details.salary_period || 'YEAR',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };
  const onInput = (field) => (e) => set(field)(e.target.value);

  // Countries outside the short list still need to be selectable when the
  // job already carries one.
  const countries = useMemo(() => {
    const code = (details.country_code || '').toUpperCase();
    return code && !COUNTRIES.some(([c]) => c === code) ? [...COUNTRIES, [code, code]] : COUNTRIES;
  }, [details.country_code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      onSaved(await saveListingDetails(jobId, form));
    } catch (err) {
      if (err.fields) setErrors(err.fields);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remote = form.workplace_type === 'remote';
  const salaryPreview = salaryLabel(form, options);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Workplace" error={errors.workplace_type}>
          <Select value={form.workplace_type} onValueChange={set('workplace_type')}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.workplace_types.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Employment type" error={errors.employment_type}>
          <Select value={form.employment_type} onValueChange={set('employment_type')}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.employment_types.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          label={remote ? 'City (optional for remote roles)' : 'City'}
          error={errors.city}
          htmlFor="broadcast-city"
        >
          <Input id="broadcast-city" value={form.city} onChange={onInput('city')} className="h-9" maxLength={120} />
        </FormField>
        <FormField label="State or region" error={errors.region} htmlFor="broadcast-region">
          <Input id="broadcast-region" value={form.region} onChange={onInput('region')} className="h-9" maxLength={120} />
        </FormField>

        <FormField label="Country" error={errors.country_code}>
          <Select value={form.country_code} onValueChange={set('country_code')}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {countries.map(([code, name]) => (
                <SelectItem key={code} value={code}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Postal code (optional)" error={errors.postal_code} htmlFor="broadcast-postal">
          <Input id="broadcast-postal" value={form.postal_code} onChange={onInput('postal_code')} className="h-9" maxLength={20} />
        </FormField>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium text-foreground">Salary (optional)</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <FormField label="Minimum" error={errors.salary_min} htmlFor="broadcast-salary-min">
            <Input
              id="broadcast-salary-min"
              type="number"
              min="0"
              inputMode="numeric"
              value={form.salary_min}
              onChange={onInput('salary_min')}
              className="h-9"
            />
          </FormField>
          <FormField label="Maximum" error={errors.salary_max} htmlFor="broadcast-salary-max">
            <Input
              id="broadcast-salary-max"
              type="number"
              min="0"
              inputMode="numeric"
              value={form.salary_max}
              onChange={onInput('salary_max')}
              className="h-9"
            />
          </FormField>
          <FormField label="Currency" error={errors.salary_currency} htmlFor="broadcast-currency">
            <Input
              id="broadcast-currency"
              value={form.salary_currency}
              onChange={(e) => set('salary_currency')(e.target.value.toUpperCase().slice(0, 3))}
              className="h-9 uppercase"
              maxLength={3}
            />
          </FormField>
          <FormField label="Period" error={errors.salary_period}>
            <Select value={form.salary_period} onValueChange={set('salary_period')}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.salary_periods.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {salaryPreview
            ? `Shown as ${salaryPreview}. Enter full amounts, not lakhs.`
            : 'Leave both blank to list the job without a salary. Enter full amounts, not lakhs.'}
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={saving} className="gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save details
        </Button>
      </div>
    </form>
  );
}

function FormField({ label, error, htmlFor, children }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function PanelSkeleton({ className }) {
  return (
    <div className={cn('space-y-5', className)} aria-busy="true" aria-label="Loading broadcast status">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-56" />
        </div>
      </div>
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-48 w-full rounded-xl" />
      <Skeleton className="h-24 w-full rounded-xl" />
    </div>
  );
}
